import { DealRequest, DealStatus } from '@/app/api/types';
import {
  checkIsDealWithoutRegistry,
  checkIsDocsSignedInDeal,
} from '@/shared/single-deal/lib/check-deal-flags';

const APPROVAL_STEP_INDEX = 1;

const PENDING_APPROVAL_STATUSES: DealStatus[] = [
  'active',
  'draft',
  'underReview',
  'waitingForClarification',
  'waitingForFactorAcceptance',
];

const APPROVED_STATUSES: DealStatus[] = [
  'contractCreated',
  'contractCreatedByFactor',
  'contractRejected',
  'waitContragentSign',
  'waitingForBuyerAcceptance',
];

const DOCS_SIGNED_STATUSES: DealStatus[] = [
  'completed',
  'partiallyPaid',
  'overdue',
  'paid',
  'partiallyRedeemed',
  'redeemed',
];

const PAID_OUT_STATUSES: DealStatus[] = [
  'paid',
  'partiallyRedeemed',
  'redeemed',
];

const REPAID_STATUSES: DealStatus[] = ['redeemed'];

const isEverySignerSigned = (doc?: DealRequest['factoringContract']) => {
  const signers = doc?.signers ?? [];
  return signers.length > 0 && signers.every((signer) => !!signer.signedAt);
};

const isDeclinedByFactor = (row: DealRequest): boolean =>
  !!row.factorDeclinedAt || !!row.factorDeclineReason;

const isApproved = (row: DealRequest): boolean =>
  !!row.factorAcceptedAt ||
  checkIsDocsSignedInDeal(row) ||
  APPROVED_STATUSES.includes(row.status) ||
  DOCS_SIGNED_STATUSES.includes(row.status);

const isAllDocsSigned = (row: DealRequest): boolean =>
  DOCS_SIGNED_STATUSES.includes(row.status) ||
  (isEverySignerSigned(row.factoringContract) &&
    (checkIsDealWithoutRegistry(row) || isEverySignerSigned(row.debtRegistry)));

const isFinanced = (row: DealRequest): boolean =>
  !!row.payoutAt ||
  row.sellerReceivedAmount > 0 ||
  row.payoutAmount > 0 ||
  PAID_OUT_STATUSES.includes(row.status);

const isRepaid = (row: DealRequest): boolean =>
  REPAID_STATUSES.includes(row.status);

const STEP_CHECKS: ((row: DealRequest) => boolean)[] = [
  () => true,
  isApproved,
  isAllDocsSigned,
  isFinanced,
  isRepaid,
];

export const getCurrentStep = (row: DealRequest): number => {
  if (PENDING_APPROVAL_STATUSES.includes(row.status) || isDeclinedByFactor(row))
    return APPROVAL_STEP_INDEX;

  const lastDoneStep = STEP_CHECKS.reduce(
    (acc, isDone, index) => (isDone(row) ? index : acc),
    -1
  );
  return lastDoneStep + 1;
};
